import React, { useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TooltipAvatar from '@/app/components/questionnaireV2/icons/tooltip-avatar';

interface HeaderTooltipProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  iconColor: string;
}

const getIconColorValue = (colorClass: string): string => {
  switch (colorClass) {
    case 'bg-catch-avocado-400':
      return '#7cf1c7';
    case 'bg-catch-yellow':
      return '#f5e695';
    case 'bg-catch-salmon':
      return '#ff9473';
    default:
      return 'currentColor';
  }
};

const HeaderTooltip: React.FC<HeaderTooltipProps> = ({
  isOpen,
  onClose,
  title,
  description,
  iconColor
}) => {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="header-tooltip-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ transitionDuration: 0.3, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="risk-tooltip-title"
          aria-describedby="risk-tooltip-description"
        >
          <motion.div
            className="header-tooltip-modal"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className="header-tooltip-close-btn"
              onClick={onClose}
              aria-label="Close modal"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                className="w-6 h-6"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Content */}
            <div className="header-tooltip-content">
              {/* Icon */}
              <div className="header-tooltip-icon-container">
                <div className="flex justify-center items-center px-4 py-2">
                  <TooltipAvatar color={getIconColorValue(iconColor)} />
                </div>
              </div>
            </div>

            {/* Header */}
            <div className="header-tooltip-header">
              <div className="header-tooltip-text-container">
                <div className="header-tooltip-title-group">
                  <h2
                    id="risk-tooltip-title"
                    className="header-tooltip-title"
                  >
                    {title}
                  </h2>
                  <p
                    id="risk-tooltip-description"
                    className="header-tooltip-description"
                  >
                    {description}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="header-tooltip-actions">
              {/* Divider */}
              <div className="header-tooltip-spacer" />

              {/* Close Button */}
              <div className="header-tooltip-button-container">
                <button
                  className="btn-tooltip-modal"
                  onClick={onClose}
                >
                  <span className="body-medium text-light">
                    Close
                  </span>
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default HeaderTooltip;
